/**
 * AdminProCarerProfiles — JCC Admin Pro Carer Profiles review.
 *
 * Scope for this step: list/open submitted profiles, plus Approve and
 * Request changes (with a short carer-visible explanation + email
 * notification). Does NOT publish anything publicly and does NOT
 * alter the member-facing profile form beyond showing the feedback.
 *
 * Only submitted profiles are shown (drafts are the member's private
 * scratchpad, same convention as Care Needs admin — once approved or
 * returned for changes, a profile leaves this "needs review" list).
 * General location (broad area only) is shown in the list and detail
 * panel; a proper Family Profile Directory is a separate future step.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle2, MessageSquareWarning, Loader2, MapPin, Eye, EyeOff, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const STATUS_LABELS = {
  submitted: "Submitted — awaiting review",
  draft: "Draft",
  approved: "Approved",
  changes_requested: "Changes requested",
};

function TierBadge({ tier, badge }) {
  if (!badge) return null;
  const cls = tier === "pro_carer" ? "bg-purple/10 text-purple" : "bg-slate-100 text-ink";
  return <span className={`pill text-xs font-semibold shrink-0 ${cls}`} data-testid="admin-pcp-tier-badge">{badge}</span>;
}

const STATUS_TABS = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Needs review" },
  { value: "approved", label: "Approved" },
  { value: "changes_requested", label: "Changes requested" },
];

function EnquiriesOversightPanel() {
  const [enquiries, setEnquiries] = useState(null);
  const [invitations, setInvitations] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: e }, { data: i }] = await Promise.all([
          api.get("/api/admin/pro-carer-profiles/enquiries"),
          api.get("/api/admin/care-needs/invitations"),
        ]);
        setEnquiries(e.enquiries || []);
        setInvitations(i.invitations || []);
      } catch {
        setEnquiries([]);
        setInvitations([]);
      }
    })();
  }, []);

  if (enquiries === null || invitations === null) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-purple" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-enquiries-oversight-panel">
      <p className="text-xs text-ink-muted max-w-2xl">
        Read-only, for safeguarding/support visibility only — there is no assignment, case-management, intervention
        or messaging here. Enquiries and invitations are single, structured first approaches; there is no reply
        thread to review.
      </p>
      <div>
        <h2 className="font-display font-semibold text-lg mb-3">Profile enquiries ({enquiries.length})</h2>
        {enquiries.length === 0 ? (
          <p className="text-sm text-ink-muted" data-testid="admin-enquiries-empty">None yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm" data-testid="admin-enquiries-table">
              <thead className="bg-slate-50 text-left text-xs text-ink-muted">
                <tr><th className="p-2.5">Family</th><th className="p-2.5">Carer</th><th className="p-2.5">Title</th><th className="p-2.5">Status</th><th className="p-2.5">Sent</th></tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.enquiry_id} className="border-t border-slate-100" data-testid={`admin-enquiry-row-${e.enquiry_id}`}>
                    <td className="p-2.5">{e.family_email}</td>
                    <td className="p-2.5">{e.carer_email}</td>
                    <td className="p-2.5">{e.title}</td>
                    <td className="p-2.5">{e.status}</td>
                    <td className="p-2.5">{fmtDateTime(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div>
        <h2 className="font-display font-semibold text-lg mb-3">Care Need invitations ({invitations.length})</h2>
        {invitations.length === 0 ? (
          <p className="text-sm text-ink-muted" data-testid="admin-invitations-empty">None yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm" data-testid="admin-invitations-table">
              <thead className="bg-slate-50 text-left text-xs text-ink-muted">
                <tr><th className="p-2.5">Family</th><th className="p-2.5">Carer</th><th className="p-2.5">Post ID</th><th className="p-2.5">Status</th><th className="p-2.5">Sent</th></tr>
              </thead>
              <tbody>
                {invitations.map((i) => (
                  <tr key={i.invitation_id} className="border-t border-slate-100" data-testid={`admin-invitation-row-${i.invitation_id}`}>
                    <td className="p-2.5">{i.family_email}</td>
                    <td className="p-2.5">{i.carer_email}</td>
                    <td className="p-2.5">{i.post_id}</td>
                    <td className="p-2.5">{i.status}</td>
                    <td className="p-2.5">{fmtDateTime(i.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminProCarerProfiles() {
  const [viewMode, setViewMode] = useState("profiles");
  const [profiles, setProfiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusTab, setStatusTab] = useState("submitted");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [actioning, setActioning] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [tabCounts, setTabCounts] = useState({});

  const load = useCallback(async (status = statusTab) => {
    setLoaded(false);
    try {
      const { data } = await api.get("/api/admin/pro-carer-profiles", { params: { status } });
      setProfiles(data.profiles || []);
      setLoaded(true);
      return data.profiles || [];
    } catch (e) {
      toast.error("Could not load Carer Profiles.");
      setLoaded(true);
      return [];
    }
  }, [statusTab]);

  const openProfile = useCallback(async (profileId) => {
    setSelectedId(profileId);
    setDetailLoading(true);
    setShowFeedbackForm(false);
    setFeedbackNote("");
    try {
      const { data } = await api.get(`/api/admin/pro-carer-profiles/${profileId}`);
      setDetail(data);
    } catch (e) {
      toast.error("Could not load this profile.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const switchTab = async (value) => {
    setStatusTab(value);
    setSelectedId(null);
    setDetail(null);
    await load(value);
  };

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/pro-carer-profiles/counts");
      setTabCounts(data.counts || {});
    } catch {
      // Best-effort badge only — silent failure keeps the tabs usable.
    }
  }, []);

  useEffect(() => {
    (async () => {
      const all = await load("submitted");
      const deepLinkId = searchParams.get("profile");
      if (deepLinkId && all.find((p) => p.profile_id === deepLinkId)) {
        openProfile(deepLinkId);
      }
    })();
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approveProfile = async () => {
    if (!selectedId) return;
    setActioning(true);
    try {
      const { data } = await api.post(`/api/admin/pro-carer-profiles/${selectedId}/approve`);
      setDetail(data);
      toast.success(data.email_sent ? "Profile approved — carer notified by email." : "Profile approved.");
      // Refresh the "needs review" list in the background — the
      // now-approved profile will naturally drop off it — but keep
      // the detail panel showing this profile's outcome so the admin
      // can see the confirmation before picking another one.
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve this profile.");
    } finally { setActioning(false); }
  };

  const submitRequestChanges = async () => {
    if (!selectedId || !feedbackNote.trim()) {
      toast.error("Please enter a short explanation for the carer.");
      return;
    }
    setActioning(true);
    try {
      const { data } = await api.post(`/api/admin/pro-carer-profiles/${selectedId}/request-changes`, {
        feedback_note: feedbackNote.trim(),
      });
      setDetail(data);
      setShowFeedbackForm(false);
      setFeedbackNote("");
      toast.success(data.email_sent ? "Changes requested — carer notified by email." : "Changes requested.");
      // Same as approveProfile — refresh the list, keep the detail
      // panel visible so the admin sees the outcome + note they sent.
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send this feedback.");
    } finally { setActioning(false); }
  };

  const togglePublicVisibility = async () => {
    if (!selectedId || !detail) return;
    setActioning(true);
    try {
      const action = detail.publicly_visible === false ? "unpause" : "pause";
      const { data } = await api.post(`/api/admin/pro-carer-profiles/${selectedId}/${action}`);
      setDetail(data);
      toast.success(action === "pause" ? "Profile hidden from the family directory." : "Profile made visible again.");
      load(statusTab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update visibility.");
    } finally { setActioning(false); }
  };

  const deleteProfile = async () => {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/pro-carer-profiles/${selectedId}`);
      toast.success("Carer Profile permanently deleted.");
      setSelectedId(null);
      setDetail(null);
      load(statusTab);
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete this profile.");
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="page-admin-pro-carer-profiles">
      <PageHero eyebrow="Admin" title="Carer Profiles"
                description="Submitted Carer Profiles awaiting review — Carer Profile Members and Pro Carers alike. Approve a profile or request changes with a short note back to the carer.">
        <Link to="/admin" className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="admin-pcp-back-home">
          <ArrowLeft className="w-4 h-4" /> Admin home
        </Link>
      </PageHero>

      <section className="section pt-8">
        <div className="container-jcc max-w-6xl">
          <div className="flex items-center gap-2 mb-6" data-testid="admin-pcp-view-tabs">
            <button
              type="button"
              onClick={() => setViewMode("profiles")}
              className={`text-sm font-semibold pb-2 border-b-2 transition-colors ${viewMode === "profiles" ? "border-purple text-purple" : "border-transparent text-ink-muted hover:text-ink"}`}
              data-testid="admin-pcp-view-tab-profiles"
            >
              Profile reviews
            </button>
            <button
              type="button"
              onClick={() => setViewMode("enquiries")}
              className={`text-sm font-semibold pb-2 border-b-2 transition-colors ml-4 ${viewMode === "enquiries" ? "border-purple text-purple" : "border-transparent text-ink-muted hover:text-ink"}`}
              data-testid="admin-pcp-view-tab-enquiries"
            >
              Enquiries & invitations
            </button>
          </div>
          {viewMode === "enquiries" && <EnquiriesOversightPanel />}
          {viewMode === "profiles" && (
          <>
          <div className="flex items-center gap-2 mb-5" data-testid="admin-pcp-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => switchTab(t.value)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors inline-flex items-center gap-1.5 ${
                  statusTab === t.value ? "bg-purple text-white border-purple" : "border-slate-200 text-ink-muted hover:border-purple/40"
                }`}
                data-testid={`admin-pcp-tab-${t.value}`}
              >
                {t.label}
                {tabCounts[t.value] > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                      statusTab === t.value ? "bg-white/25 text-white" : t.value === "submitted" ? "bg-amber-500 text-white" : "bg-slate-200 text-ink"
                    }`}
                    data-testid={`admin-pcp-tab-count-${t.value}`}
                  >
                    {tabCounts[t.value]}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <ul className="space-y-2" data-testid="admin-pcp-list">
              {loaded && profiles.length === 0 && (
                <li className="text-ink-muted text-sm py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="admin-pcp-empty">
                  No {STATUS_TABS.find((t) => t.value === statusTab)?.label.toLowerCase()} profiles.
                </li>
              )}
              {profiles.map((p) => (
                <li key={p.profile_id}>
                  <button
                    className={`w-full text-left rounded-xl border p-3.5 hover:bg-slate-50 transition-colors ${
                      selectedId === p.profile_id ? "border-purple bg-purple/5" : "border-slate-200"
                    }`}
                    onClick={() => openProfile(p.profile_id)}
                    data-testid={`admin-pcp-select-${p.profile_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{p.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <TierBadge tier={p.membership_tier} badge={p.badge} />
                        {p.status === "approved" && p.publicly_visible === false && (
                          <span className="pill bg-amber-100 text-amber-800 shrink-0 text-xs" data-testid={`admin-pcp-hidden-${p.profile_id}`}>Hidden</span>
                        )}
                        <span className="pill bg-teal/10 text-teal shrink-0 text-xs">
                          {STATUS_LABELS[p.status] || p.status}
                        </span>
                      </div>
                    </div>
                    {p.general_location && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-ink-muted" data-testid={`admin-pcp-location-${p.profile_id}`}>
                        <MapPin className="w-3 h-3" /> {p.general_location}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-1.5 text-xs text-ink-muted">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {p.status === "draft" ? `Last updated ${fmtDate(p.updated_at)}` : `Submitted ${fmtDate(p.submitted_at)}`}
                      </span>
                      <span className="text-purple font-medium" data-testid={`admin-pcp-open-${p.profile_id}`}>Open / Review</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            <div>
              {!selectedId ? (
                <p className="text-ink-muted">Select a profile to review.</p>
              ) : detailLoading ? (
                <p className="text-ink-muted">Loading…</p>
              ) : !detail ? (
                <p className="text-ink-muted">Could not load this profile.</p>
              ) : (
                <div className="card-base p-5 space-y-4 text-sm" data-testid="admin-pcp-detail">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {detail.photo_data_url ? (
                        <img src={detail.photo_data_url} alt="" className="w-14 h-14 rounded-full object-cover border border-slate-200" data-testid="admin-pcp-photo" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-ink-muted text-xs">No photo</div>
                      )}
                      <div>
                        <div className="font-display text-lg font-semibold">{detail.name}</div>
                        <div className="text-ink-muted text-xs">{detail.owner_email}</div>
                      </div>
                    </div>
                    <span className="pill bg-teal/10 text-teal shrink-0" data-testid="admin-pcp-status-pill">{STATUS_LABELS[detail.status] || detail.status}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <TierBadge tier={detail.membership_tier} badge={detail.badge} />
                    {detail.status === "approved" && (
                      <button
                        type="button"
                        onClick={togglePublicVisibility}
                        disabled={actioning}
                        className="btn-outline text-xs inline-flex items-center gap-1.5 !py-1.5 !px-3"
                        data-testid="admin-pcp-toggle-visibility-btn"
                      >
                        {detail.publicly_visible === false
                          ? <><Eye className="w-3.5 h-3.5" /> Unpause — make visible</>
                          : <><EyeOff className="w-3.5 h-3.5" /> Pause — hide from directory</>}
                      </button>
                    )}
                  </div>
                  {detail.status === "approved" && (
                    <p className="text-xs text-ink-muted" data-testid="admin-pcp-visibility-note">
                      {detail.publicly_visible === false
                        ? "Currently hidden from the family Carer Profiles directory."
                        : "Currently visible in the family Carer Profiles directory."}
                    </p>
                  )}

                  {detail.status === "submitted" && (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={approveProfile}
                        disabled={actioning}
                        className="btn-teal text-sm inline-flex items-center gap-1.5"
                        data-testid="admin-pcp-approve-btn"
                      >
                        {actioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowFeedbackForm((s) => !s)}
                        disabled={actioning}
                        className="btn-outline text-sm inline-flex items-center gap-1.5"
                        data-testid="admin-pcp-request-changes-btn"
                      >
                        <MessageSquareWarning className="w-4 h-4" /> Request changes
                      </button>
                    </div>
                  )}

                  {showFeedbackForm && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 space-y-2" data-testid="admin-pcp-feedback-form">
                      <label className="block">
                        <span className="text-xs font-medium text-amber-900">
                          Short explanation for the carer (they will see this on their profile and by email)
                        </span>
                        <textarea
                          value={feedbackNote}
                          onChange={(e) => setFeedbackNote(e.target.value)}
                          rows={3}
                          className="mt-1.5 w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-400"
                          placeholder="e.g. Please add a little more detail to your About You section."
                          data-testid="admin-pcp-feedback-note-input"
                        />
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={submitRequestChanges}
                          disabled={actioning}
                          className="btn-purple text-sm"
                          data-testid="admin-pcp-feedback-submit-btn"
                        >
                          {actioning ? "Sending…" : "Send feedback"}
                        </button>
                        <button type="button" onClick={() => setShowFeedbackForm(false)} className="text-xs text-ink-muted hover:text-ink" data-testid="admin-pcp-feedback-cancel-btn">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {detail.status === "approved" && (
                    <p className="text-xs text-teal font-medium" data-testid="admin-pcp-approved-note">
                      Approved{detail.reviewed_at ? ` on ${fmtDateTime(detail.reviewed_at)}` : ""}. The carer has been notified.
                    </p>
                  )}
                  {detail.status === "changes_requested" && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="admin-pcp-changes-requested-note">
                      <p className="font-medium">Changes requested{detail.reviewed_at ? ` on ${fmtDateTime(detail.reviewed_at)}` : ""}.</p>
                      <p className="mt-1 whitespace-pre-wrap">{detail.feedback_note}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 border-y border-slate-100 py-3">
                    <div data-testid="admin-pcp-detail-location"><span className="text-ink-muted text-xs block">General location</span>{detail.general_location || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Submitted</span>{fmtDateTime(detail.submitted_at)}</div>
                    <div><span className="text-ink-muted text-xs block">Last updated</span>{fmtDateTime(detail.updated_at)}</div>
                    <div><span className="text-ink-muted text-xs block">Availability</span>{detail.availability_status || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Willing to travel</span>{detail.willing_to_travel || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Earliest start date</span>{detail.earliest_start_date || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">DBS status</span>{detail.dbs_status || "—"}</div>
                    <div><span className="text-ink-muted text-xs block">Insurance status</span>{detail.insurance_status || "—"}</div>
                  </div>

                  <div>
                    <span className="text-ink-muted text-xs block mb-1">Care types</span>
                    <div className="flex flex-wrap gap-1.5">
                      {(detail.care_types || []).length > 0
                        ? detail.care_types.map((t) => <span key={t} className="pill bg-slate-100 text-ink text-xs">{t}</span>)
                        : <span className="text-ink-muted">—</span>}
                    </div>
                  </div>

                  <div>
                    <span className="text-ink-muted text-xs block mb-1">Preferred work pattern</span>
                    <p className="whitespace-pre-wrap">{detail.preferred_work_pattern || "—"}</p>
                  </div>

                  <div>
                    <span className="text-ink-muted text-xs block mb-1">About you</span>
                    <p className="whitespace-pre-wrap">{detail.about_you || "—"}</p>
                  </div>

                  <div>
                    <span className="text-ink-muted text-xs block mb-1">Care experience</span>
                    <p className="whitespace-pre-wrap">{detail.care_experience_description || "—"}</p>
                  </div>

                  {detail.is_pro && (
                    <div className="rounded-xl border border-purple/20 bg-purple/5 p-3.5 space-y-3" data-testid="admin-pcp-pro-fields">
                      <div className="text-xs font-semibold text-purple">Pro Carer — enhanced details</div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Qualifications & training</span>
                        <p className="whitespace-pre-wrap">{detail.qualifications_training || "—"}</p>
                      </div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Additional specialisms</span>
                        <p className="whitespace-pre-wrap">{detail.additional_specialisms || "—"}</p>
                      </div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Professional background</span>
                        <p className="whitespace-pre-wrap">{detail.professional_background || "—"}</p>
                      </div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Detailed working preferences</span>
                        <p className="whitespace-pre-wrap">{detail.detailed_rota_preferences || "—"}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><span className="text-ink-muted text-xs block">Indicative rates</span>{detail.indicative_rates || "—"}</div>
                        <div><span className="text-ink-muted text-xs block">Languages spoken</span>{detail.languages_spoken || "—"}</div>
                        <div><span className="text-ink-muted text-xs block">Driving licence</span>{detail.has_driving_licence == null ? "—" : detail.has_driving_licence ? "Yes" : "No"}</div>
                        <div><span className="text-ink-muted text-xs block">Own vehicle</span>{detail.has_vehicle == null ? "—" : detail.has_vehicle ? "Yes" : "No"}</div>
                        <div><span className="text-ink-muted text-xs block">DBS certificate date</span>{detail.dbs_certificate_date || "—"}</div>
                        <div><span className="text-ink-muted text-xs block">DBS update service</span>{detail.dbs_update_service || "—"}</div>
                        <div><span className="text-ink-muted text-xs block">Insurance type</span>{detail.insurance_type || "—"}</div>
                        <div><span className="text-ink-muted text-xs block">Insurance renewal date</span>{detail.insurance_renewal_date || "—"}</div>
                      </div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Wider travel information</span>
                        <p className="whitespace-pre-wrap">{detail.travel_radius_detail || "—"}</p>
                      </div>
                      <div>
                        <span className="text-ink-muted text-xs block mb-1">Testimonials</span>
                        <p className="whitespace-pre-wrap">{detail.testimonials || "—"}</p>
                      </div>
                      <p className="text-[11px] text-ink-soft">Carer-declared — not independently verified by JCC unless stated otherwise.</p>
                    </div>
                  )}

                  <div>
                    <span className="text-ink-muted text-xs block mb-1">Preferred contact method(s)</span>
                    <div className="flex flex-wrap gap-1.5">
                      {(detail.preferred_contact_methods || []).length > 0
                        ? detail.preferred_contact_methods.map((t) => <span key={t} className="pill bg-slate-100 text-ink text-xs">{t}</span>)
                        : <span className="text-ink-muted">—</span>}
                    </div>
                  </div>

                  <div className="text-xs text-ink-muted">
                    Accuracy declaration: {detail.declaration_consent ? "Confirmed by member" : "Not confirmed"}
                  </div>

                  <div className="pt-3 border-t border-slate-100">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          disabled={deleting}
                          className="text-red-600 hover:text-red-700 text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid="admin-pcp-delete-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete profile permanently
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="admin-pcp-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Permanently delete this Carer Profile?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You are about to permanently delete <strong>{detail.name}</strong>'s Carer Profile
                            {detail.owner_email ? <> ({detail.owner_email})</> : null}. This removes the profile
                            content and its published listing for good — it cannot be undone. Their JCC account,
                            membership/subscription and any existing Care Discussion Rooms are NOT affected, and
                            they can start a brand-new profile at any time afterwards.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="admin-pcp-delete-cancel">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={deleteProfile}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-400"
                            data-testid="admin-pcp-delete-confirm"
                          >
                            {deleting ? "Deleting…" : "Delete permanently"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </section>
    </div>
  );
}
